import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark, PackageOpen, Percent, TrendingUp } from 'lucide-react';
import { AmountCard } from '../../common/page_component/index.js';
import dashboardService from '../../../services/DashboardService.js';
import { buildDateParams } from './DateRangeFilter.js';
import { fullCurrency } from '../../../utils/formatCurrency.js';

const ROLES = ['Admin', 'Hub Admin', 'Accountant', 'Agent', 'System Admin'];
const money = (value) => fullCurrency(value || 0);

const gridClass = (count) => {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 md:grid-cols-3';
  if (count === 4) return 'grid-cols-2 lg:grid-cols-4';
  if (count === 5) return 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5';
  return 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6';
};

/**
 * Handling, commission, GST/TDS, then actual revenue.
 * Loads on its own so the shipment and revenue cards do not wait.
 */
const CompanyFinanceExtras = ({
  user,
  dateRange = 'all_time',
  customStart = '',
  customEnd = '',
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const hidden = !user || !ROLES.includes(user.role);

  useEffect(() => {
    if (hidden) return undefined;
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    let cancelled = false;
    dashboardService.getOwnerOverview(buildDateParams(dateRange, customStart, customEnd))
      .then((res) => {
        if (!cancelled) setData(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => { cancelled = true; };
  }, [hidden, user?.user_id, user?.company_id, dateRange, customStart, customEnd]);

  if (hidden || !data) return null;

  const showHandling = Number(data.handling) > 0;
  const showCommission = Number(data.commission) > 0;
  const showGst = Number(data.gst) > 0;
  const showTds = Number(data.tds) > 0;
  const showTax = showGst || showTds;
  const cardCount = 1 + (showHandling ? 1 : 0) + (showCommission ? 1 : 0) + (showTax ? 1 : 0);

  return (
    <>
      <div className={`grid ${gridClass(cardCount)} gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 items-stretch`}>
        {showHandling ? (
          <AmountCard
            label="Handling"
            amount={data.handling}
            icon={<PackageOpen className="w-5 h-5 text-white" />}
            color="bg-sky-600"
            subAmount={data.loading}
            subLabel="Loading"
            subAmount2={data.unloading}
            subLabel2="Unloading"
          />
        ) : null}
        {showCommission ? (
          <AmountCard
            label="Commission"
            amount={data.commission}
            icon={<Percent className="w-5 h-5 text-white" />}
            color="bg-teal-600"
            subAmount={data.booking_commission}
            subLabel="Booking"
            subAmount2={data.delivery_commission}
            subLabel2="Delivery"
            onClick={() => navigate('/agent-commission-breakdown')}
          />
        ) : null}
        {showTax ? (
          <AmountCard
            label={showGst && showTds ? 'GST & TDS' : (showGst ? 'GST' : 'TDS')}
            amount={Number(data.gst || 0) + Number(data.tds || 0)}
            icon={<Landmark className="w-5 h-5 text-white" />}
            color="bg-slate-600"
            subAmount={showGst && showTds ? data.gst : undefined}
            subLabel={showGst && showTds ? 'GST' : undefined}
            subAmount2={showGst && showTds ? data.tds : undefined}
            subLabel2={showGst && showTds ? 'TDS' : undefined}
          />
        ) : null}
        <AmountCard
          label="Actual revenue"
          amount={data.actual_revenue}
          icon={<TrendingUp className="w-5 h-5 text-white" />}
          color="bg-emerald-600"
        />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs text-gray-500">Recovered from agents</p>
          <p className="mt-1 text-lg font-semibold text-emerald-800">{money(data.recovered_from_agents)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs text-gray-500">Still due from agents</p>
          <p className="mt-1 text-lg font-semibold text-amber-800">{money(data.due_from_agents)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs text-gray-500">Recovered from credit clients</p>
          <p className="mt-1 text-lg font-semibold text-emerald-800">{money(data.recovered_from_clients)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs text-gray-500">Still due from credit clients</p>
          <p className="mt-1 text-lg font-semibold text-amber-800">{money(data.due_from_clients)}</p>
        </div>
      </div>
    </>
  );
};

export default CompanyFinanceExtras;
